import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { banksApi, type BankMaster, type UpsertBankInput } from '../lib/api/banks';
import { useToast } from '../lib/use-toast';
import { TableActionMenu } from './TableActionMenu';

interface EditState {
  mode: 'add' | 'edit';
  id?: string;
  code: string;
  name: string;
  shortName: string;
  active: boolean;
  sortOrder: number;
}

const EMPTY: EditState = { mode: 'add', code: '', name: '', shortName: '', active: true, sortOrder: 999 };

export function BanksSection() {
  const { user } = useAuth();
  const { toast, showToast } = useToast();
  const canEdit = user?.role === 'KTT';

  const [items, setItems] = useState<BankMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [modal, setModal] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await banksApi.list();
      setItems(r);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => setModal({ ...EMPTY, sortOrder: items.length + 1 });
  const openEdit = (b: BankMaster) => setModal({
    mode: 'edit', id: b.id, code: b.code, name: b.name,
    shortName: b.shortName ?? '', active: b.active, sortOrder: b.sortOrder,
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modal) return;
    if (!modal.code.trim() || !modal.name.trim()) {
      showToast('Cần nhập mã + tên NH'); return;
    }
    setSaving(true);
    try {
      const payload: UpsertBankInput = {
        code: modal.code.trim().toUpperCase(),
        name: modal.name.trim(),
        shortName: modal.shortName.trim() || null,
        active: modal.active,
        sortOrder: modal.sortOrder,
      };
      if (modal.mode === 'edit' && modal.id) {
        await banksApi.update(modal.id, payload);
        showToast('Đã cập nhật NH');
      } else {
        await banksApi.create(payload);
        showToast('Đã thêm NH');
      }
      setModal(null); load();
    } catch (e2) { showToast(e2 instanceof Error ? e2.message : String(e2)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (b: BankMaster) => {
    try {
      await banksApi.update(b.id, { active: !b.active });
      showToast(b.active ? 'Đã ẩn NH' : 'Đã hiển thị NH');
      load();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e)); }
  };

  const remove = async (b: BankMaster) => {
    if (!window.confirm(`Xoá "${b.name}"? NV đang dùng NH này sẽ không bị ảnh hưởng nhưng dropdown sẽ mất option này.`)) return;
    try {
      await banksApi.remove(b.id);
      showToast('Đã xoá NH');
      load();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <div className="ct-card">
      <div className="ct-card-head">
        <div>
          <h3>Ngân hàng</h3>
          <p className="sal-card-sub">Danh sách NH VN dùng cho NV chọn TK chi lương. KTT quản lý.</p>
        </div>
        {canEdit && (
          <button className="pay-btn-dark" type="button" onClick={openAdd}>+ Thêm NH</button>
        )}
      </div>

      {err && <div className="pay-table-error">Lỗi: {err}</div>}

      <div className="pay-table-wrap">
        {loading ? <div className="pay-empty">Đang tải…</div> : items.length === 0 ? (
          <div className="pay-empty">Chưa có NH nào.</div>
        ) : (
          <table className="pay-tbl banks-tbl">
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th style={{ width: 100 }}>Mã</th>
                <th>Tên NH</th>
                <th style={{ width: 100 }}>Ngắn</th>
                <th style={{ width: 120 }}>Trạng thái</th>
                {canEdit && <th style={{ width: 120, textAlign: 'right' }}>Thao tác</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id}>
                  <td style={{ fontFamily: 'ui-monospace, monospace', color: '#94a3b8' }}>{b.sortOrder}</td>
                  <td><span className="hr-code-pill">{b.code}</span></td>
                  <td>{b.name}</td>
                  <td style={{ color: '#64748b' }}>{b.shortName ?? '—'}</td>
                  <td>
                    <span className={`hr-status-dot ${b.active ? 'green' : 'gray'}`}>
                      <span className="dot" /> {b.active ? 'Đang bật' : 'Đã ẩn'}
                    </span>
                  </td>
                  {canEdit && (
                    <td style={{ textAlign: 'right' }}>
                      <TableActionMenu
                        open={actionMenuId === b.id}
                        onToggle={() => setActionMenuId((current) => current === b.id ? null : b.id)}
                        onClose={() => setActionMenuId(null)}
                        ariaLabel={`Thao tác ${b.name}`}
                        items={[
                          { label: 'Sửa ngân hàng', onClick: () => openEdit(b) },
                          { label: b.active ? 'Ẩn khỏi dropdown' : 'Hiển thị trong dropdown', onClick: () => toggleActive(b) },
                          { label: 'Xoá ngân hàng', tone: 'danger', dividerBefore: true, onClick: () => remove(b) },
                        ]}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div className="cvm-backdrop" onClick={() => setModal(null)}>
          <div className="cvm-modal" style={{ width: 480 }} onClick={(e) => e.stopPropagation()}>
            <form onSubmit={submit}>
              <div className="cvm-head">
                <div>
                  <h2>{modal.mode === 'add' ? 'Thêm NH' : `Sửa NH ${modal.code}`}</h2>
                  <p>Danh sách NH VN cho NV chọn TK chi lương.</p>
                </div>
                <button type="button" className="cvm-close" onClick={() => setModal(null)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="cvm-body">
                <div className="afm-grid-2">
                  <div className="pfm-field">
                    <label>Mã NH <span className="req">*</span></label>
                    <input value={modal.code} onChange={(e) => setModal({ ...modal, code: e.target.value.toUpperCase() })} placeholder="VCB, TCB, MB…" disabled={modal.mode === 'edit'} required />
                  </div>
                  <div className="pfm-field">
                    <label>Rút gọn</label>
                    <input value={modal.shortName} onChange={(e) => setModal({ ...modal, shortName: e.target.value })} placeholder="VCB" />
                  </div>
                </div>
                <div className="pfm-field">
                  <label>Tên đầy đủ <span className="req">*</span></label>
                  <input value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} placeholder="Vietcombank" required />
                </div>
                <div className="afm-grid-2">
                  <div className="pfm-field">
                    <label>Thứ tự</label>
                    <input type="number" min={1} value={modal.sortOrder} onChange={(e) => setModal({ ...modal, sortOrder: Math.max(1, Number(e.target.value) || 1) })} />
                  </div>
                  <div className="pfm-field">
                    <label className="hr-checkbox">
                      <input type="checkbox" checked={modal.active} onChange={(e) => setModal({ ...modal, active: e.target.checked })} />
                      <span>Hiển thị trong dropdown</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="cvm-foot">
                <button type="button" className="pay-btn-outline" onClick={() => setModal(null)} disabled={saving}>Huỷ</button>
                <button type="submit" className="pay-btn-primary" disabled={saving}>{saving ? 'Đang lưu…' : (modal.mode === 'add' ? 'Thêm' : 'Cập nhật')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && <div className="coa-toast">{toast}</div>}
    </div>
  );
}
